import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import { User } from '@/types';

// Mock entities - replace with actual API calls
const SurveyAPI = {
  get: async (id: string) => ({} as Survey),
};

const SurveyResponseAPI = {
  create: async (data: any) => ({} as any),
};

const UserAPI = {
  me: async () => ({} as User),
};

interface Survey {
  id: string;
  title: string;
  description: string;
  course_id?: string;
  questions: Array<{
    question: string;
    type: 'rating' | 'multiple_choice' | 'text';
    required: boolean;
    options?: string[];
  }>;
}

interface SurveyResponseData {
  question_index: number;
  answer: string;
}

export default function TakeSurvey() {
  const location = useLocation();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [responses, setResponses] = useState<(SurveyResponseData | null)[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadSurveyAndUser = async () => {
      const searchParams = new URLSearchParams(location.search);
      const surveyId = searchParams.get('surveyId');

      if (!surveyId) {
        toast.error('No survey ID provided.');
        setLoading(false);
        return;
      }

      try {
        const [surveyData, currentUser] = await Promise.all([
          SurveyAPI.get(surveyId),
          UserAPI.me(),
        ]);

        if (surveyData) {
          setSurvey(surveyData);
          setResponses(Array(surveyData.questions.length).fill(null));
        } else {
          toast.error('Survey not found.');
        }
        
        setUser(currentUser);

      } catch (error) {
        console.error('Error loading survey:', error);
        toast.error('Failed to load the survey.');
      } finally {
        setLoading(false);
      }
    };

    loadSurveyAndUser();
  }, [location.search]);

  const handleResponseChange = (questionIndex: number, value: string) => {
    const newResponses = [...responses];
    newResponses[questionIndex] = { question_index: questionIndex, answer: value };
    setResponses(newResponses);
  };

  const handleSubmit = async () => {
    if (!survey || !user) return;
    
    setIsSubmitting(true);
    
    const requiredQuestions = survey.questions.map((q, i) => ({...q, index: i})).filter(q => q.required);
    const missingResponses = requiredQuestions.filter(q => !responses[q.index]?.answer);

    if (missingResponses.length > 0) {
        toast.error("Please answer all required questions before submitting.");
        setIsSubmitting(false);
        return;
    }

    try {
      const finalResponses = responses.filter((r): r is SurveyResponseData => r !== null);
      await SurveyResponseAPI.create({
        survey_id: survey.id,
        user_id: user.id,
        course_id: survey.course_id,
        responses: finalResponses,
        completion_date: new Date().toISOString(),
      });
      
      toast.success('Your survey response has been submitted.');
      navigate(createPageUrl('MyLearning'));

    } catch (error) {
      console.error('Error submitting survey:', error);
      toast.error('Failed to submit your response.');
      setIsSubmitting(false);
    }
  };
  
  const renderQuestion = (question: Survey['questions'][0], index: number) => {
    const isRequired = question.required;

    switch (question.type) {
      case 'rating':
        return (
          <div key={index} className="space-y-3">
            <Label className="text-foreground">
              {question.question}
              {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <RadioGroup onValueChange={(value) => handleResponseChange(index, value)} className="flex gap-6">
              {[1, 2, 3, 4, 5].map(value => (
                <div key={value} className="flex flex-col items-center space-y-1">
                  <RadioGroupItem value={String(value)} id={`q${index}-v${value}`} />
                  <Label htmlFor={`q${index}-v${value}`} className="text-sm">{value}</Label>
                </div>
              ))}
            </RadioGroup>
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>Strongly Disagree</span>
              <span>Strongly Agree</span>
            </div>
          </div>
        );
      case 'multiple_choice':
        return (
          <div key={index} className="space-y-3">
            <Label className="text-foreground">
              {question.question}
              {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <RadioGroup onValueChange={(value) => handleResponseChange(index, value)} className="space-y-2">
              {question.options?.map((option, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <RadioGroupItem value={option} id={`q${index}-o${i}`} />
                  <Label htmlFor={`q${index}-o${i}`} className="cursor-pointer">{option}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
      case 'text':
        return (
          <div key={index} className="space-y-3">
            <Label htmlFor={`q${index}`} className="text-foreground">
              {question.question}
              {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Textarea
              id={`q${index}`}
              onChange={(e) => handleResponseChange(index, e.target.value)}
              placeholder="Your response..."
              className="min-h-[100px]"
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6 max-w-4xl mx-auto">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">Survey not found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      <Card className="shadow-lg bg-gradient-card border-primary/10">
        <CardHeader>
          <CardTitle className="text-2xl text-foreground">{survey.title}</CardTitle>
          <CardDescription className="text-muted-foreground">{survey.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {survey.questions.map((q, i) => renderQuestion(q, i))}
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className="bg-gradient-primary hover:bg-primary/90"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Response'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}